import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Plus, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { SingleShotType } from "../../../types/public-types";
import { uploadStoryboardApi } from "../../../Services/StoryboardService/StoryBoardService";
import toast from "react-hot-toast";

interface UploadRow {
  id: number;
  selectedShotId: string;
  file: File | null;
  previewUrl: string;
}

type Props = {
  shotList: SingleShotType[];
  onClose: () => void;
  singleSelectedShot: SingleShotType;
  projectId: string;
  breakdownId: string;
  updateRefreshCounter: () => void;
};

const BulkUploadStoryBoardModal = ({
  shotList,
  onClose,
  singleSelectedShot,
  projectId,
  breakdownId,
  updateRefreshCounter,
}: Props) => {
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([
    { id: Date.now(), selectedShotId: "", file: null, previewUrl: "" },
  ]);

  const handleAddRow = () => {
    setUploadRows((prev) => [
      ...prev,
      { id: Date.now(), selectedShotId: "", file: null, previewUrl: "" },
    ]);
  };

  const handleRemoveRow = (id: number) => {
    if (uploadRows.length > 1) {
      setUploadRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  const handleShotChange = (rowId: number, shotId: string) => {
    setUploadRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, selectedShotId: shotId } : row,
      ),
    );
  };

  const handleFileChange = (rowId: number, file: File | null) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setUploadRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, file, previewUrl } : row,
      ),
    );
  };

  const handleSave = async () => {
    const validUploads = uploadRows
      .filter((row): row is UploadRow & { file: File } =>
        Boolean(row.selectedShotId && row.file),
      )
      .map((row) => ({
        selectedShotId: row.selectedShotId,
        file: row.file,
        fileName: row.file.name,
      }));
    if (validUploads.length === 0) {
      return;
    }

    const data: Record<string, string | string[]> = {
      projectId,
      breakdownId,
    };
    validUploads.forEach((upload) => {
      const shotId = upload.selectedShotId;
      const existing = data[shotId];
      if (!existing) {
        data[shotId] = [upload.fileName];
      } else {
        (data[shotId] as string[]).push(upload.fileName);
      }
    });

    try {
      const response = await uploadStoryboardApi(data);
      const signedUrlsByShot = response.data as Record<
        string,
        Record<string, string>
      >;

      const fileByShotAndName = new Map<string, File>();
      validUploads.forEach(({ selectedShotId, file, fileName }) => {
        fileByShotAndName.set(`${selectedShotId}:${fileName}`, file);
      });

      const uploadPromises: Promise<void>[] = [];
      for (const [shotId, filenameToUrl] of Object.entries(signedUrlsByShot)) {
        for (const [fileName, signedUrl] of Object.entries(filenameToUrl)) {
          const file = fileByShotAndName.get(`${shotId}:${fileName}`);
          if (file) {
            uploadPromises.push(handleUploadStoryboard(signedUrl, file));
          }
        }
      }

      await Promise.all(uploadPromises);
      toast.success("Storyboards uploaded successfully");
      onClose();

      for (let i = 0; i < 2; i++) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        updateRefreshCounter();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload storyboards");
    }
  };

  const handleUploadStoryboard = async (signedUrl: string, file: File) => {
    const res = await fetch(signedUrl, {
      method: "PUT",
      body: file,
    });
    if (!res.ok) {
      throw new Error("Failed to upload storyboard");
    }
  };

  useEffect(() => {
    if (singleSelectedShot) {
      setUploadRows([
        {
          id: Date.now(),
          selectedShotId: singleSelectedShot.shotId,
          file: null,
          previewUrl: "",
        },
      ]);
    }
  }, [singleSelectedShot]);

  // Get already selected shot IDs to prevent duplicate selection
  const selectedShotIds = uploadRows.map((row) => row.selectedShotId);

  return (
    <Box
      sx={{
        width: "500px",
        maxHeight: "80vh",
        borderRadius: "20px",
        backgroundColor: "#1B1D28",
        padding: "24px",
        fontFamily: "Poppins",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontFamily: "Poppins",
            fontWeight: 600,
          }}
        >
          Upload Storyboards
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <X size={20} />
        </IconButton>
      </Box>

      {/* Upload Rows */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxHeight: "400px",
          overflowY: "auto",
          pr: 1,
        }}
      >
        {uploadRows.map((row, index) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              backgroundColor: "#252A38",
              borderRadius: "10px",
              padding: "12px",
            }}
          >
            {/* Shot Dropdown */}
            <FormControl sx={{ flex: 1 }}>
              <Select
                value={row.selectedShotId}
                onChange={(e) => handleShotChange(row.id, e.target.value)}
                displayEmpty
                sx={{
                  color: "white",
                  backgroundColor: "#1B1D28",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#606479",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6C65D9",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6C65D9",
                  },
                  "& .MuiSelect-icon": {
                    color: "#606479",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#252A38 !important",
                      color: "white",
                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "#3A3F4F",
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#6C65D9 !important",
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select Shot
                </MenuItem>
                {shotList.map((shot) => (
                  <MenuItem key={shot.shotId} value={shot.shotId}>
                    {shot.shotNumberText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Image Preview or Upload Button */}
            {row.previewUrl ? (
              <Box
                sx={{
                  position: "relative",
                  width: "60px",
                  height: "60px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={row.previewUrl}
                  alt="Preview"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <Box
                  onClick={() =>
                    setUploadRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id
                          ? { ...r, file: null, previewUrl: "" }
                          : r,
                      ),
                    )
                  }
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.9)",
                    },
                  }}
                >
                  <X size={10} fill="#fff" />
                </Box>
              </Box>
            ) : (
              <>
                <label htmlFor={`upload-${row.id}`}>
                  <Box
                    sx={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "8px",
                      border: "2px dashed #606479",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "#6C65D9",
                        backgroundColor: "rgba(108, 101, 217, 0.1)",
                      },
                    }}
                  >
                    <UploadSimple size={24} fill="#606479" />
                  </Box>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  id={`upload-${row.id}`}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleFileChange(row.id, e.target.files[0]);
                    }
                  }}
                />
              </>
            )}

            {/* Remove Row Button */}
            {uploadRows.length > 1 && (
              <IconButton
                onClick={() => handleRemoveRow(row.id)}
                sx={{
                  color: "#606479",
                  "&:hover": {
                    color: "#ff4d4d",
                  },
                }}
              >
                <Trash size={20} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      {/* Add Row Button */}
      <Button
        onClick={handleAddRow}
        startIcon={<Plus size={18} />}
        sx={{
          color: "#6C65D9",
          border: "1px dashed #6C65D9",
          borderRadius: "10px",
          textTransform: "none",
          fontFamily: "Poppins",
          "&:hover": {
            backgroundColor: "rgba(108, 101, 217, 0.1)",
          },
        }}
      >
        Add Another Shot
      </Button>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          marginTop: "8px",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            minWidth: "120px",
            padding: "8px 24px",
            borderRadius: "10px",
            fontFamily: "Poppins",
            border: "1px solid #6C65D9",
            color: "#6C65D9",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(108, 101, 217, 0.1)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!uploadRows.some((row) => row.selectedShotId && row.file)}
          sx={{
            minWidth: "120px",
            padding: "8px 24px",
            borderRadius: "10px",
            fontFamily: "Poppins",
            backgroundColor: "#6C65D9",
            color: "white",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#5a54c7",
            },
            "&:disabled": {
              backgroundColor: "#3A3F4F",
              color: "#606479",
            },
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default BulkUploadStoryBoardModal;
